import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Carousel, Collapse } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { MENU_ITEMS, MENU_ITEMS_QT } from "./menuData";
import menuRules from "../assets/images/menu-rules.png";
import ls01 from "../assets/images/ls_01.png";
import ls02 from "../assets/images/ls_02.png";
import ls03 from "../assets/images/ls_03.png";
import ls04 from "../assets/images/ls_04.png";
import sl01 from "../assets/images/sl-c-layer-one.png";
import menuCasino from "../assets/images/menu-99998.png";
import { toast } from "react-toastify";
import {
  getSeriesInfoBySportsName,
  fetchSocialMediaContent,
} from "../store/slices/sidebarSlice";
import { isFeatureHidden } from "../b2bconfig";

const Sidebar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { matchesBySport, matches } = useSelector((state) => state.matches);
  const { user, token, isDemoLogin } = useSelector((state) => state.auth);
  const logoUrl = useSelector((state) => state.logo?.logoUrl);
  const isDemoUser = !!user?.is_demo || !!user?.isDemo || !!isDemoLogin;
  const logoLoading = useSelector((state) => state.logo?.loading);
  const { sportsInfo, seriesBySport, slides, unauthorized } = useSelector(
    (state) => state.sidebar,
  );
  const whatsappUrl = useSelector((state) => state.sidebar?.whatsappUrl || "");

  const [openSectionId, setOpenSectionId] = useState(null);
  const [openSubIds, setOpenSubIds] = useState({});
  const hasAuth = !!(user || token);
  const isLoggedOut = !hasAuth;

  const pathname = location?.pathname || "";

  const activeSport = useSelector((state) => state?.sidebar?.sportsInfo);

  const isSportAvailable = (id) => {
    return activeSport?.some((sport) => sport.sport_id === id);
  };

  const activeSportIdFromUrl = useMemo(() => {
    // /sports/:id
    const sportsMatch = pathname.match(/^\/sports\/([^/]+)/);
    if (sportsMatch?.[1]) return sportsMatch[1];

    // /sports/detail/:sportId/:matchId OR /sports/detail/:matchId
    const detailMatch = pathname.match(/^\/sports\/detail\/([^/]+)/);
    if (detailMatch?.[1]) return detailMatch[1];

    return null;
  }, [pathname]);

  const isSidebarItemActive = (item) => {
    if (!item) return false;
    if (item.isLink && item.to) {
      return pathname === item.to || pathname.startsWith(`${item.to}/`);
    }
    if (activeSportIdFromUrl && item.id) {
      return String(item.id) === String(activeSportIdFromUrl);
    }
    return false;
  };

  const formatMarketTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const formatVenueLabel = (market) => {
    const venue = market?.venue || "Unknown";
    const ccRaw = market?.country_code
      ? String(market.country_code).toUpperCase()
      : "";
    const cc =
      ccRaw === "AU"
        ? "AUS"
        : ccRaw === "ZA"
          ? "ZA"
          : ccRaw === "FR"
            ? "FR"
            : ccRaw === "GB"
              ? "GB"
              : ccRaw === "IE"
                ? "IE"
                : ccRaw;
    const dateStr = market?.market_start_time
      ? new Date(market.market_start_time).toLocaleDateString([], {
          day: "numeric",
          month: "long",
        })
      : "";
    const meta = [cc, dateStr].filter(Boolean).join(" ");
    return meta ? `${venue} (${meta})` : venue;
  };

  const formatMarketChildLabel = (market) => {
    const venue = market?.venue || "Unknown";
    const ccRaw = market?.country_code
      ? String(market.country_code).toUpperCase()
      : "";
    const cc =
      ccRaw === "AU"
        ? "AUS"
        : ccRaw === "ZA"
          ? "ZA"
          : ccRaw === "FR"
            ? "FR"
            : ccRaw === "GB"
              ? "GB"
              : ccRaw === "IE"
                ? "IE"
                : ccRaw;
    const dateStr = market?.market_start_time
      ? new Date(market.market_start_time).toLocaleDateString([], {
          day: "numeric",
          month: "long",
        })
      : "";
    const timeStr = formatMarketTime(market?.market_start_time);
    const venueMeta = [cc, dateStr].filter(Boolean).join(" ");
    const left = venueMeta ? `${venue} (${venueMeta})` : venue;
    return timeStr ? `${left} (${timeStr})` : left;
  };

  const buildSubItemsFromSeries = (sportId) => {
    const key = sportId != null ? String(sportId) : undefined;
    const seriesList = (key && seriesBySport?.[key]) || [];

    if (!seriesList.length) {
      if (unauthorized) {
        // Not logged in / unauthorized: show a simple "No series" item
        return [
          {
            id: `sport-${sportId}-no-series`,
            label: "No series",
            links: [
              {
                label: "No series",
                to: "/",
              },
            ],
          },
        ];
      }
      return [];
    }

    // Racing (Horse/Greyhound): show same style as racing page:
    // group by venue, and show time boxes as children
    const sid = Number(sportId);
    const isRacingSport = sid === 7 || sid === 4339;
    if (isRacingSport) {
      let list = seriesList;
      if (!Array.isArray(list) && list && typeof list === "object") {
        list = Object.values(list);
      }
      if (!Array.isArray(list)) return [];

      const byVenue = list.reduce((acc, m) => {
        const venueKey = m?.venue || "Unknown";
        if (!acc[venueKey]) acc[venueKey] = [];
        acc[venueKey].push(m);
        return acc;
      }, {});

      return Object.entries(byVenue).map(([venue, markets], index) => {
        const first = markets?.[0];
        return {
          id: `sport-${sportId}-venue-${index}`,
          label: formatVenueLabel(first) || venue,
          links: (markets || []).map((m) => {
            const label =
              formatMarketChildLabel(m) ||
              formatMarketTime(m?.market_start_time) ||
              m?.name ||
              "—";
            const matchId = m?.match_id || "";
            const marketId =
              m?.market_id || m?.marketId || m?.market_id_str || "";
            const marketStartTime =
              m?.market_start_time || m?.marketStartTime || "";

            const qp = new URLSearchParams();
            if (marketId) qp.set("marketId", String(marketId));
            if (marketStartTime)
              qp.set("marketStartTime", String(marketStartTime));

            const qs = qp.toString();
            return {
              label,
              // For racing (Horse & Greyhound), detail route is /sports/detail/:sportId/:matchId
              to: qs
                ? `/sports/detail/${sportId}/${matchId}?${qs}`
                : `/sports/detail/${sportId}/${matchId}`,
            };
          }),
        };
      });
    }

    // Create one subItem per series/market so that all names
    // appear directly under the sport (no extra "Series" tab)
    const subItems = seriesList.map((s, index) => {
      const label = s.series_name || s.market_name || s.name || "No markets";
      return {
        id: `sport-${sportId}-series-${index}`,
        label,
        links: [
          {
            label,
            to: `/sports/detail/${s?.match_id || ""}`,
          },
        ],
      };
    });

    // Optional "Others" entry can be re-enabled later if needed
    // subItems.push({
    //   id: `sport-${sportId}-series-others`,
    //   label: "Others",
    //   links: [
    //     {
    //       label: "Others",
    //       to: "/",
    //     },
    //   ],
    // });

    return subItems;
  };

  const normalizeSportName = (name) => {
    const key = String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[\s\W_]+/g, "");

    // Common aliases / casing differences between static vs API
    if (key === "soccer") return "football";
    if (key === "sportbook") return "sportsbook";
    return key;
  };

  // When logged out, `sportsInfo` may be empty, so static `MENU_ITEMS`
  // use non-numeric ids like `collapse0`. We still need numeric sport ids
  // (matching `matches[].event_type_id` / `sport_id`) to build submenus.
  const getSportIdForMenuLabel = (label) => {
    const k = normalizeSportName(label);
    const map = {
      football: 1,
      tennis: 2,
      cricket: 4,
      rugby: 5,
      horseracing: 7,
      tabletennis: 20,
      futsal: 29,
      darts: 3503,
      kabaddi: 99994,
      binary: 99990,
      politics: 2378961,
      sportsbook: 99991,
      baseball: 7511,
      basketball: 7522,
      volleyball: 998917,
      icehockey: 7524,
      mixedmartialarts: 26420387,
      greyhoundracing: 4339,
    };
    return map[k];
  };

  const getMatchesForSeries = (sportId, seriesLabel) => {
    if (!sportId || !seriesLabel) return [];

    const allMatchesForSport =
      matchesBySport?.[String(sportId)] || matchesBySport?.[sportId] || [];

    if (!Array.isArray(allMatchesForSport) || !allMatchesForSport.length) {
      return [];
    }

    const normalizedTarget = String(seriesLabel).trim().toLowerCase();

    return allMatchesForSport.filter((match) => {
      const seriesName =
        match?.series_name ||
        match?.competition_name ||
        match?.sport_name ||
        "";

      if (!seriesName) return false;

      const normalizedSeries = String(seriesName).trim().toLowerCase();
      if (normalizedSeries !== normalizedTarget) return false;

      const matchName =
        match?.name ||
        match?.match_name ||
        match?.market_name ||
        match?.series_name ||
        "";

      const normalizedMatchName = String(matchName).trim().toLowerCase();

      if (!normalizedMatchName) return true;

      return normalizedMatchName !== normalizedTarget;
    });
  };

  const buildSubItemsFromMatchesForSport = (sportId) => {
    const sportIdStr = sportId != null ? String(sportId) : "";
    const sportIdNum = Number(sportIdStr);
    const isRacingSport =
      Number.isFinite(sportIdNum) && (sportIdNum === 7 || sportIdNum === 4339);

    const allMatchesForSport = Array.isArray(matches)
      ? matches.filter(
          (m) => String(m?.event_type_id ?? m?.sport_id ?? "") === sportIdStr,
        )
      : [];

    if (!allMatchesForSport.length) return [];

    if (isRacingSport) {
      // Racing (logged-out): group by venue/track, then render time boxes as children.
      // Logged-out `matches` items are not guaranteed to have `venue` populated,
      // so we use multiple fallbacks to avoid everything becoming "Unknown".
      const getMatchVenue = (m) =>
        m?.venue ||
        m?.race_venue ||
        m?.venue_name ||
        m?.track_name ||
        m?.track ||
        m?.racecourse ||
        m?.meeting_name ||
        m?.region ||
        m?.city ||
        // Last-resort fallbacks: some racing payloads may store the meeting/venue
        // inside the generic match name fields.
        m?.name ||
        m?.match_name ||
        m?.event_name ||
        m?.event ||
        "Unknown";

      const getTimeIso = (m) =>
        m?.market_start_time || m?.match_date || m?.open_date || null;

      const formatCcForVenue = (ccRaw) => {
        const ccStr = ccRaw ? String(ccRaw).toUpperCase() : "";
        return ccStr === "AU"
          ? "AUS"
          : ccStr === "ZA"
            ? "ZA"
            : ccStr === "FR"
              ? "FR"
              : ccStr === "GB"
                ? "GB"
                : ccStr === "IE"
                  ? "IE"
                  : ccStr || "";
      };

      const formatVenueLabelFromMatch = (m) => {
        const venue = getMatchVenue(m);
        const cc = formatCcForVenue(m?.country_code);
        const timeIso = getTimeIso(m);
        const dateStr = timeIso
          ? new Date(timeIso).toLocaleDateString([], {
              day: "numeric",
              month: "long",
            })
          : "";
        const meta = [cc, dateStr].filter(Boolean).join(" ");
        return meta ? `${venue} (${meta})` : venue;
      };

      const byVenue = allMatchesForSport.reduce((acc, m) => {
        const venueKey = getMatchVenue(m);
        if (!acc[venueKey]) acc[venueKey] = [];
        acc[venueKey].push(m);
        return acc;
      }, {});

      return Object.entries(byVenue).map(([venue, markets], index) => {
        const sortedMarkets = Array.isArray(markets)
          ? [...markets].sort((a, b) => {
              const aTime = getTimeIso(a);
              const bTime = getTimeIso(b);
              const aDate = aTime ? new Date(aTime) : null;
              const bDate = bTime ? new Date(bTime) : null;
              if (!aDate && !bDate) return 0;
              if (!aDate) return 1;
              if (!bDate) return -1;
              return aDate - bDate;
            })
          : [];

        const first = sortedMarkets?.[0];
        return {
          id: `sport-${sportIdStr}-venue-${index}`,
          label: formatVenueLabelFromMatch(first) || venue,
          links: (sortedMarkets || []).map((m, mIndex) => ({
            id: m?.match_id || m?.event_id || m?.id || mIndex,
            label:
              formatMarketTime(getTimeIso(m)) ||
              m?.market_name ||
              m?.name ||
              "—",
            // RacingDetail uses the route param `subId` as matchId; eventTypeId is ignored.
            to: (() => {
              const matchId =
                m?.match_id || m?.event_id || m?.id || m?.eventId || "";
              const marketId =
                m?.market_id || m?.marketId || m?.market_id_str || "";
              const marketStartTime =
                m?.market_start_time || m?.marketStartTime || "";

              const qp = new URLSearchParams();
              if (marketId) qp.set("marketId", String(marketId));
              if (marketStartTime)
                qp.set("marketStartTime", String(marketStartTime));

              const qs = qp.toString();
              return qs
                ? `/sports/detail/${sportIdStr}/${matchId}?${qs}`
                : `/sports/detail/${sportIdStr}/${matchId}`;
            })(),
          })),
        };
      });
    }

    // Non-racing: group by series/competition label.
    const bySeries = allMatchesForSport.reduce((acc, m) => {
      const seriesName =
        m?.series_name || m?.competition_name || m?.sport_name || "";
      const normalizedKey = String(seriesName).trim().toLowerCase();
      if (!normalizedKey) return acc;

      if (!acc[normalizedKey]) {
        acc[normalizedKey] = { label: seriesName, matches: [] };
      }
      acc[normalizedKey].matches.push(m);
      return acc;
    }, {});

    return Object.values(bySeries).map((entry, index) => ({
      id: `sport-${sportIdStr}-series-${index}`,
      label: entry?.label || "No markets",
      links: (entry?.matches || []).map((match, matchIndex) => ({
        id: match?.match_id || match?.event_id || match?.id || matchIndex,
        to: `/sports/detail/${
          match?.match_id || match?.event_id || match?.id || ""
        }`,
        label:
          match?.name ||
          match?.match_name ||
          match?.market_name ||
          match?.series_name ||
          "—",
      })),
    }));
  };

  const menuItemsToRender = useMemo(() => {
    const apiList = Array.isArray(sportsInfo) ? sportsInfo : [];

    const noMatchSubItems = (sectionId) => [
      {
        id: `${sectionId}-no-match`,
        label: "No match found",
        links: [
          {
            label: "No match found",
            to: "#",
          },
        ],
      },
    ];

    const apiByName = new Map();
    apiList.forEach((s) => {
      const key = normalizeSportName(s?.name);
      if (key && !apiByName.has(key)) apiByName.set(key, s);
    });

    const openedId = openSectionId != null ? String(openSectionId) : null;
    const openedSportId =
      openedId && Number.isFinite(Number(openedId)) ? Number(openedId) : null;
    const openedSubItems =
      openedSportId != null ? buildSubItemsFromSeries(openedSportId) : [];

    // Logged out (unauthorized response): show static list, but no submenu data
    // (subsidebar should stay empty).
    if (isLoggedOut) {
      return (isSportAvailable("QT") ? MENU_ITEMS_QT : MENU_ITEMS || []).map(
        (it) => {
          if (it?.isLink) return it;

          const sportIdFromLabel = getSportIdForMenuLabel(it?.label);
          const idStr =
            sportIdFromLabel != null ? String(sportIdFromLabel) : it?.id;

          const isOpened = openedId && String(idStr) === openedId;
          if (!isOpened) {
            return { ...it, id: idStr, subItems: [] };
          }

          const logoutSubItems = buildSubItemsFromMatchesForSport(idStr);
          return {
            ...it,
            id: idStr,
            subItems: logoutSubItems?.length
              ? logoutSubItems
              : noMatchSubItems(idStr),
          };
        },
      );
    }

    // Logged in (authorized response):
    // 1) render ALL static MENU_ITEMS in the same order
    // 2) for static sports, if API has a matching sport_id, use it for id so getSeries works
    // 3) append any API sports not present in static at the bottom
    // Build a set of ALL static labels (including link items like Casino)
    // so we can avoid duplicating them when appending API extras.
    const staticKeys = new Set(
      (isSportAvailable("QT") ? MENU_ITEMS_QT : MENU_ITEMS || [])
        .map((it) => normalizeSportName(it?.label))
        .filter(Boolean),
    );

    const staticRendered = (
      isSportAvailable("QT") ? MENU_ITEMS_QT : MENU_ITEMS || []
    ).map((it) => {
      if (it?.isLink) return it;

      const key = normalizeSportName(it?.label);

      const apiSport = key ? apiByName.get(key) : undefined;
      const sportId = apiSport?.sport_id;

      // If API sport exists, bind the numeric sport id so click -> getSeries works.
      // Otherwise keep the static id (non-numeric) and leave submenu empty.
      if (sportId != null && sportId !== "") {
        const idStr = String(sportId);
        return {
          ...it,
          id: idStr,
          subItems:
            openedId === idStr
              ? openedSubItems?.length
                ? openedSubItems
                : noMatchSubItems(idStr)
              : [],
        };
      }

      return {
        ...it,
        subItems:
          openedId && String(it?.id) === openedId
            ? noMatchSubItems(it?.id || "item")
            : [],
      };
    });

    const extras = apiList
      .filter((s) => {
        const key = normalizeSportName(s?.name);
        return key && !staticKeys.has(key);
      })
      .map((s) => ({
        id: String(s?.sport_id),
        label: s?.name,
        icon: s?.is_live_sport === 1 ? menuCasino : s?.icon || sl01,
        isLink: s?.is_live_sport === 1,
        ...(s?.is_live_sport === 1
          ? {
              to:
                s?.sport_id === "QT"
                  ? "/casino?category=QTECH&providerCode=QT&subCategory=ALL"
                  : s?.sport_id === "GAP"
                    ? "/casino?category=GAP&providerCode=GAP&subCategory=ALL"
                    : "/casino?category=ALL&providerCode=MAC88&subCategory=Aviator+X",
            }
          : {}),
        subItems:
          openedId === String(s?.sport_id)
            ? openedSubItems?.length
              ? openedSubItems
              : noMatchSubItems(String(s?.sport_id || "item"))
            : [],
      }));

    return [...staticRendered, ...extras];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportsInfo, seriesBySport, openSectionId, matches, isLoggedOut]);

  const toggleSection = (id) => {
    setOpenSectionId((prev) => (prev === id ? null : id));
    setOpenSubIds((prev) => ({ ...prev, [id]: undefined }));
  };

  const toggleSub = (sectionId, subId) => {
    setOpenSubIds((prev) => ({
      ...prev,
      [sectionId]: prev[sectionId] === subId ? null : subId,
    }));
  };

  const toggleSidebar = () => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("toggle-sidebar");
  };

  const handleWhatsappClick = (e) => {
    e.preventDefault();
    if (!whatsappUrl) {
      toast.error("Whatsapp link is not available right now.");
      return;
    }
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    dispatch(fetchSocialMediaContent(hasAuth));
  }, [dispatch, hasAuth]);

  const carouselSlides =
    Array.isArray(slides) && slides.length
      ? slides
      : [
          { _id: "fallback-1", description: ls01, title: "slide-1" },
          { _id: "fallback-2", description: ls02, title: "slide-2" },
          { _id: "fallback-3", description: ls03, title: "slide-3" },
          { _id: "fallback-4", description: ls04, title: "slide-4" },
        ];

  return (
    <aside id="sidebar" className="sidebar">
      <ul id="sidebar-nav" className="sidebar-nav">
        {logoUrl && (
          <img
            className="mobile-logo bg-black "
            style={{ width: "70%" }}
            src={
              logoUrl ||
              (logoLoading
                ? ""
                : "https://speedcdn.io/assets/logos/reddybook.club.png")
            }
            alt="logo"
          />
        )}

        <i
          className="bi bi-list-nested sidebar-toggle-btn"
          onClick={toggleSidebar}
        ></i>
        <li className="nav-item"></li>

        {menuItemsToRender.map((item) => {
          if (item.label === "QTech" || item.label === "MAC88") return null;
          const isActive = isSidebarItemActive(item);
          if (item.isLink) {
            return (
              <li key={item.id} className="nav-item">
                <Link
                  to={item.to || "/"}
                  className={`nav-link ${item.highlight ? "hightlight-smenu" : ""} ${isActive ? "active" : ""}`}
                  onClick={(e) => {
                    const to = item.to || "";
                    if (
                      to.includes("/casino") ||
                      to.includes("/dc/gamev1.1/")
                    ) {
                      if (!hasAuth) {
                        e.preventDefault();
                        if (
                          typeof window !== "undefined" &&
                          typeof window.openLoginDialog === "function"
                        ) {
                          window.openLoginDialog();
                        } else {
                          toast.error("Please log in first to access Casino.");
                        }
                      } else if (isDemoUser) {
                        e.preventDefault();
                        toast.error(
                          "Demo users are not allowed to play casino games.",
                        );
                      }
                    }
                  }}
                >
                  <img src={item.icon} alt={item.label} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          }
          return (
            <li key={item.id} className="nav-item">
              <Link
                to={`#${item.id}`}
                className={`nav-link ${isActive ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  // Load series/markets list for this sport when user clicks it
                  if (hasAuth && item.id) {
                    const sportId = Number(item.id);
                    if (Number.isFinite(sportId)) {
                      const isRacingMarket = sportId === 7 || sportId === 4339;
                      dispatch(
                        getSeriesInfoBySportsName({ sportId, isRacingMarket }),
                      );
                    }
                  }
                  toggleSection(item.id);
                }}
                aria-expanded={openSectionId === item.id}
                aria-controls={item.id}
              >
                <img src={item.icon} alt={item.label} />
                <span>{item.label}</span>
                <i className="bi bi-caret-down ms-auto"></i>
              </Link>
              <Collapse in={openSectionId === item.id}>
                <ul id={item.id} className="nav-content">
                  {(item.subItems || []).map((subItem) => {
                    const sportIdNum = Number(item.id);
                    const isRacingSport =
                      Number.isFinite(sportIdNum) &&
                      (sportIdNum === 7 || sportIdNum === 4339);

                    let linksToRender;

                    if (isRacingSport) {
                      // Keep existing behaviour for Horse Racing and Greyhound
                      linksToRender = subItem.links || [];
                    } else if (isLoggedOut && Array.isArray(subItem.links)) {
                      // Logged-out users: show links derived from `state.matches.matches`.
                      linksToRender = subItem.links || [];
                    } else {
                      const seriesMatches = getMatchesForSeries(
                        item.id,
                        subItem.label,
                      );

                      if (seriesMatches.length > 0) {
                        linksToRender = seriesMatches.map((match) => ({
                          to: `/sports/detail/${
                            match?.match_id ||
                            match?.event_id ||
                            match?.id ||
                            ""
                          }`,
                          label:
                            match?.name ||
                            match?.match_name ||
                            match?.market_name ||
                            match?.series_name ||
                            "—",
                        }));
                      } else {
                        // No matches for this series: show explicit "No matches"
                        linksToRender = [
                          {
                            to: "#",
                            label: "No matches",
                            disabled: true,
                          },
                        ];
                      }
                    }

                    return (
                      <li key={subItem.id}>
                        <Link
                          onClick={() => toggleSub(item.id, subItem.id)}
                          aria-expanded={openSubIds[item.id] === subItem.id}
                          aria-controls={subItem.id}
                        >
                          <span>{subItem.label}</span>
                          <i className="bi bi-caret-down ms-auto"></i>
                        </Link>
                        <Collapse in={openSubIds[item.id] === subItem.id}>
                          <div id={subItem.id}>
                            <ul className="nav-second-level">
                              {linksToRender.map((link, linkIndex) => (
                                <li key={linkIndex}>
                                  <Link
                                    to={link.to || "/"}
                                    className="final-link"
                                    onClick={(e) => {
                                      if (link.disabled) {
                                        e.preventDefault();
                                        return;
                                      }
                                      if (!hasAuth) {
                                        e.preventDefault();
                                        toast.error(
                                          "Please login to continue.",
                                        );
                                        if (
                                          typeof window !== "undefined" &&
                                          typeof window.openLoginDialog ===
                                            "function"
                                        ) {
                                          window.openLoginDialog();
                                        }
                                      }
                                    }}
                                  >
                                    <span>{link.label}</span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </Collapse>
                      </li>
                    );
                  })}
                </ul>
              </Collapse>
            </li>
          );
        })}

        <li className="d-block d-sm-none nav-item">
          <button type="button" className="nav-link final-link">
            <img src={menuRules} alt="menu-rules" />
            <span>rules</span>
          </button>
        </li>

        {hasAuth && !isFeatureHidden("whatsapp") && (
          <li className="nav-item">
            <Link
              to={whatsappUrl || "#"}
              className="nav-link"
              onClick={handleWhatsappClick}
              aria-label="Whatsapp Support"
            >
              <i
                className="bi bi-whatsapp"
                style={{ fontSize: 16, color: "#25D366" }}
              />
              <span>Whatsapp Support</span>
            </Link>
          </li>
        )}
      </ul>
      <Carousel indicators={false} style={{ background: `url(${sl01})` }}>
        {carouselSlides.map((slide, index) => (
          <Carousel.Item key={slide?._id || slide?.slug || index}>
            <img
              src={slide?.description}
              alt={slide?.title || `slide-${index + 1}`}
              className="d-block w-100"
            />
          </Carousel.Item>
        ))}
      </Carousel>
    </aside>
  );
};

export default Sidebar;
